import { useState } from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useTranslationApp } from "../../lib/i18next";
import {
  ADMIN_DASHBOARD_CREATE_BRAND_BUTTON,
  LOGOUT,
  OWNER_DASHBOARD_NAV_AD_SPEND,
  OWNER_DASHBOARD_NAV_ADLE_CARD_WAITLIST,
  OWNER_DASHBOARD_NAV_CREATE_USER,
  OWNER_DASHBOARD_NAV_FORM,
  OWNER_DASHBOARD_NAV_GLOBAL_NOTIFICATIONS,
  OWNER_DASHBOARD_NAV_OPTIONS,
  OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES,
  OWNER_DASHBOARD_NAV_REPORTED_CONTENT,
} from "../../i18n/keysTranslations";
import {
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
  PADDING_DESKTOP,
  PADDING_MOBILE,
} from "../../utils/constants";
import LanguageSwitcher from "../Common/LanguageSwitcher";
import ModalCreateBrand from "../../pages/AdminDashboard/components/ModalCreateBrand";
import ModalCreateOwnerUser from "../../pages/AdminDashboard/components/ModalCreateOwnerUser";

const NAV_TEXT_ACTION_SX = {
  fontSize: FONT_SIZE_BUTTON,
  fontWeight: FONT_WEIGHT_BUTTON,
  lineHeight: "normal",
  mb: -0.5,
  color: "text.primary",
};

const TEXT_BUTTON_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "none",
  background: "none",
  p: 0,
  font: "inherit",
  color: "inherit",
};

export const ownerChrome = {
  toolbar: {
    px: {
      md: PADDING_DESKTOP + 3,
      xs: PADDING_MOBILE + 3,
    },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: { xs: 1.5, md: 3 },
    flexWrap: "wrap",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  logoLink: {
    display: "flex",
    width: "auto",
    justifyContent: "flex-start",
  },
  languageSwitcher: {
    width: "auto !important",
    height: "auto !important",
    minWidth: "auto !important",
    maxWidth: "none !important",
    minHeight: "auto !important",
    maxHeight: "none !important",
    p: "0 !important",
    m: 0,
    border: "none !important",
    borderRadius: "50% !important",
    lineHeight: 1,
    boxShadow: "none !important",
    backgroundColor: "transparent !important",
    "&.MuiButtonBase-root.MuiButton-root:hover": {
      border: "none !important",
      boxShadow: "none !important",
      backgroundColor: "transparent !important",
      color: "inherit !important",
    },
    "&.MuiButtonBase-root.MuiButton-root:active": {
      border: "none !important",
      boxShadow: "none !important",
      backgroundColor: "transparent !important",
    },
  },
};

const OWNER_NAV_OPTIONS = [
  {
    id: "create-user",
    labelKey: OWNER_DASHBOARD_NAV_CREATE_USER,
    action: "createUser",
  },
  {
    id: "report-content",
    labelKey: OWNER_DASHBOARD_NAV_REPORTED_CONTENT,
    to: "/report-content",
  },
  { id: "leads", labelKey: OWNER_DASHBOARD_NAV_FORM, to: "/leads" },
  {
    id: "ad-spend",
    labelKey: OWNER_DASHBOARD_NAV_AD_SPEND,
    to: "/ad-spend",
  },
  {
    id: "adle-card-waitlist",
    labelKey: OWNER_DASHBOARD_NAV_ADLE_CARD_WAITLIST,
    to: "/adle-card-waitlist",
  },
  {
    id: "promotional-codes",
    labelKey: OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES,
    to: "/promotional-codes",
  },
  {
    id: "global-notifications",
    labelKey: OWNER_DASHBOARD_NAV_GLOBAL_NOTIFICATIONS,
    to: "/global-notifications",
  },
];

const OwnerNavTextAction = ({ label, onClick, ...buttonProps }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    {...buttonProps}
    sx={TEXT_BUTTON_SX}
  >
    <Typography sx={NAV_TEXT_ACTION_SX}>{label}</Typography>
  </Box>
);

const OwnerNavActions = ({ userLanguage, onChangeLanguage, onLogout }) => {
  const { t } = useTranslationApp();
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isCreateBrandOpen, setIsCreateBrandOpen] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const closeOptions = () => {
    setOptionsAnchorEl(null);
  };

  const optionActions = {
    createUser: () => setIsCreateUserOpen(true),
  };

  const onSelectOption = (option) => {
    closeOptions();
    optionActions[option.action]?.();
  };

  return (
    <>
      <OwnerNavTextAction
        label={t(ADMIN_DASHBOARD_CREATE_BRAND_BUTTON)}
        onClick={() => setIsCreateBrandOpen(true)}
      />
      <OwnerNavTextAction
        label={t(OWNER_DASHBOARD_NAV_OPTIONS)}
        onClick={(event) => setOptionsAnchorEl(event.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(optionsAnchorEl)}
      />
      <Menu
        anchorEl={optionsAnchorEl}
        open={Boolean(optionsAnchorEl)}
        onClose={closeOptions}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ "& .MuiPaper-root": { marginTop: "8px" } }}
      >
        {OWNER_NAV_OPTIONS.map((option) =>
          option.to ? (
            <MenuItem
              key={option.id}
              component={Link}
              to={option.to}
              onClick={closeOptions}
            >
              {t(option.labelKey)}
            </MenuItem>
          ) : (
            <MenuItem key={option.id} onClick={() => onSelectOption(option)}>
              {t(option.labelKey)}
            </MenuItem>
          )
        )}
      </Menu>
      <LanguageSwitcher
        language={userLanguage || "en"}
        onChangeLanguage={onChangeLanguage}
        colorButton="secondary"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={ownerChrome.languageSwitcher}
        sxMenu={{ "& .MuiPaper-root": { marginTop: "8px" } }}
      />
      <OwnerNavTextAction label={t(LOGOUT)} onClick={onLogout} />
      <ModalCreateOwnerUser
        modalOpen={isCreateUserOpen}
        onCloseModal={() => setIsCreateUserOpen(false)}
      />
      <ModalCreateBrand
        modalOpen={isCreateBrandOpen}
        onCloseModal={() => setIsCreateBrandOpen(false)}
      />
    </>
  );
};

export default OwnerNavActions;
